import { useReimbursementRequestContext } from "../../../Contexts/ReimbursementRequestContext";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { truncateStr } from "../../../utilities/helper";
import FilePreview from "./FilePreview";
import PropTypes from "prop-types";
import dayjs from "dayjs";

function UploadFile(props) {
  const { title, name, onView } = props;

  return (
    <div className="mb-3">
      <p className="text-sm text-[#60656D] mb-1">{title}</p>
      <div className="flex items-center border rounded-md p-3">
        <p className="mr-2 text-[#60656D]">
          <InsertDriveFileOutlinedIcon />
        </p>
        <p className="mr-2">{name}</p>
        <button className="ml-auto text-[#60656D]" onClick={onView}>
          <VisibilityOutlinedIcon />
        </button>
      </div>
    </div>
  );
}

UploadFile.propTypes = {
  title: PropTypes.string,
  name: PropTypes.string,
  onView: PropTypes.func,
};

function Summary() {
  const {
    formData,
    setFormData,
    viewModalShown,
    setViewModalShown,
    base64File,
    setBase64File,
    fileName,
    setFileName,
  } = useReimbursementRequestContext();

  const handleView = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setViewModalShown(true);
      setFileName(file.name);
      setBase64File(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleViewTerms = () => {};

  const handleViewPolicy = () => {};

  const handleCheckTerms = (e) => {
    let newForm = {
      ...formData,
      termsChecked: e.target.checked,
    };
    setFormData(newForm);
  };

  let medCertFile = null;
  let soaFile = null;
  let policeReportFile = null;
  let opticalRxFile = null;
  let dentalCertFile = null;
  let opMedicineRxFile = null;

  if (formData.claimType === "Out Patient (OP)") {
    medCertFile = (
      <UploadFile
        title="Medical certificate"
        name={truncateStr(formData.medicalCertificate.name)}
        onView={() => handleView(formData.medicalCertificate)}
      />
    );
    policeReportFile = (
      <UploadFile
        title="Police report"
        name={truncateStr(formData.policeReport.name)}
        onView={() => handleView(formData.policeReport)}
      />
    );
  } else if (formData.claimType === "In Patient (IP)") {
    medCertFile = (
      <UploadFile
        title="Medical certificate"
        name={truncateStr(formData.medicalCertificate.name)}
        onView={() => handleView(formData.medicalCertificate)}
      />
    );
    soaFile = (
      <UploadFile
        title="Statement of account"
        name={truncateStr(formData.statementOfAccount.name)}
        onView={() => handleView(formData.statementOfAccount)}
      />
    );
    policeReportFile = (
      <UploadFile
        title="Police report"
        name={truncateStr(formData.policeReport.name)}
        onView={() => handleView(formData.policeReport)}
      />
    );
  } else if (formData.claimType === "OP Medicine") {
    opMedicineRxFile = (
      <UploadFile
        title="Prescription for medicines purchased"
        name={truncateStr(formData.opMedicinePrescription.name)}
        onView={() => handleView(formData.opMedicinePrescription)}
      />
    );
  } else if (formData.claimType === "Optical") {
    opticalRxFile = (
      <UploadFile
        title="Prescription for eyeglasses or contact lens"
        name={truncateStr(formData.opticalPrescription.name)}
        onView={() => handleView(formData.opticalPrescription)}
      />
    );
  } else if (formData.claimType === "Dental") {
    dentalCertFile = (
      <UploadFile
        title="Dental certificate"
        name={truncateStr(formData.dentalCertificate.name)}
        onView={() => handleView(formData.dentalCertificate)}
      />
    );
  }

  return (
    <div className="p-4">
      <p className="text-[#1F2328] text-xl font-bold mb-1">
        Reimbursement request summary
      </p>
      <p className="text-[#60656D] mb-4">
        Please review the information and attached files for your reimbursement
        and then submit the request.
      </p>
      <div className="mb-3">
        <p className="text-sm text-[#60656D] mb-1">Total amount of claim</p>
        <p>₱ {formData.claimAmount || "0"}</p>
      </div>
      <div className="mb-3">
        <p className="text-sm text-[#60656D] mb-1">Hospital/Clinic</p>
        <p className="capitalize">
          {formData.hospital.providerName.toLowerCase()}
        </p>
      </div>
      <div className="mb-3">
        <p className="text-sm text-[#60656D] mb-1">Doctor's name</p>
        <p className="capitalize">
          {formData.doctor.Physician?.split(":")[0].toLowerCase() || ""}
        </p>
      </div>
      <div className="mb-3">
        <p className="text-sm text-[#60656D] mb-1">
          Availment / admission date
        </p>
        <p>
          {(formData.admissionDate &&
            dayjs(formData.admissionDate).format("MMMM DD, YYYY")) ||
            "-"}
        </p>
      </div>
      <div className="mb-3">
        <p className="text-sm text-[#60656D] mb-1">Discharge date</p>
        <p>
          {(formData.dischargeDate &&
            dayjs(formData.dischargeDate).format("MMMM DD, YYYY")) ||
            "-"}
        </p>
      </div>
      <UploadFile
        title="Charge slips or itemized bills"
        name={truncateStr(formData.chargeSlip.name)}
        onView={() => handleView(formData.chargeSlip)}
      />
      <UploadFile
        title="Official receipts or invoices with TIN"
        name={truncateStr(formData.officialReceipt.name)}
        onView={() => handleView(formData.officialReceipt)}
      />
      {medCertFile}
      {soaFile}
      {policeReportFile}
      {opticalRxFile}
      {dentalCertFile}
      {opMedicineRxFile}
      <FilePreview
        open={viewModalShown}
        file={base64File}
        fileName={fileName}
        onClose={() => setViewModalShown(false)}
      />
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.termsChecked}
              onChange={handleCheckTerms}
            />
          }
          label={
            <p className="text-sm">
              I hereby consent to abide by the{" "}
              <button onClick={handleViewTerms} className="text-[#0869cf]">
                Terms & Conditions
              </button>{" "}
              as well as the{" "}
              <button onClick={handleViewPolicy} className="text-[#0869cf]">
                Privacy Policy
              </button>
            </p>
          }
        />
      </FormGroup>
    </div>
  );
}

export default Summary;
